import { useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { SealCheck, Copy } from "@phosphor-icons/react";

export const QuoteApprovalLinkButton = ({ quote, onSent }) => {
  const [open, setOpen] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");

  const openDialog = () => {
    setToEmail(quote?.client_email || "");
    setMessage("");
    setLink("");
    setOpen(true);
  };

  const act = async (send) => {
    setBusy(true);
    try {
      const { data } = await API.post(`/quotes/${quote.id}/approval-link`, { send, to_email: toEmail, message });
      setLink(data.link);
      if (send) {
        toast.success(`Approval link emailed to ${data.to_email}`);
        setOpen(false);
        onSent && onSent();
      } else {
        await navigator.clipboard.writeText(data.link).catch(() => {});
        toast.success("Approval link copied to clipboard");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create approval link");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openDialog} data-testid="approval-link-btn">
        <SealCheck size={14} className="mr-1" /> Client Approval Link
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="approval-link-dialog">
          <DialogHeader><DialogTitle>Client Approval Link</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Sends the client a secure link (valid 30 days) to view this quote online and approve or decline it with one click. The client PDF is attached.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Client email</Label>
              <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client@example.com" data-testid="approval-link-email" />
            </div>
            <div>
              <Label className="text-xs">Personal message (optional)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="h-20 text-sm" placeholder="Hi — here's the quote we discussed…" data-testid="approval-link-message" />
            </div>
            {link && <p className="text-[11px] text-muted-foreground break-all bg-muted/50 rounded p-2" data-testid="approval-link-url">{link}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" disabled={busy} onClick={() => act(false)} data-testid="approval-link-copy-btn">
                <Copy size={14} className="mr-1" /> Copy Link Only
              </Button>
              <Button size="sm" disabled={busy} onClick={() => act(true)} data-testid="approval-link-send-btn">
                {busy ? "Working…" : "Email to Client"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuoteApprovalLinkButton;
